import { Box, Button, CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { datasourceModalOpenState } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import {
    ExportItems,
    downloadItemsToDisk,
} from '~/utils/io/files/downloadDicom';
import {
    SeriesData,
    SeriesDataObject,
    transformPatientData,
} from '~/utils/transforms/patientDataTransforms';
import PatientAccordion from '../PatientAccordion';
import useActiveItems from '../useActiveItems';
import PatientDataTable from './DatasetTable';

export type SelectedDatasets = {
    [modality: string]: { [seriesId: string]: SeriesDataObject };
};

const createExportItems = (selectedDatasets: SelectedDatasets): ExportItems => {
    const exportItems: ExportItems = {
        images: [],
        doses: [],
        structureSets: [],
        registrations: [],
    };

    Object.entries(selectedDatasets).forEach(([modality, seriesData]) => {
        Object.values(seriesData).forEach((series) => {
            switch (modality) {
                case Modality.CT:
                case Modality.MR:
                    exportItems.images?.push(series as Image3D);
                    break;
                case Modality.RTDOSE:
                    exportItems.doses?.push(series as DoseMatrix);
                    break;
                case Modality.RTSTRUCT:
                    exportItems.structureSets?.push(series as StructureSet);
                    break;
                case Modality.REG:
                    exportItems.registrations?.push(
                        series as RigidRegistration
                    );
                    break;
                default:
                    console.warn('Unhandled modality', modality);
                    break;
            }
        });
    });

    return exportItems;
};

export default function LoadedPatientData({ patient }: { patient: Patient }) {
    const [exporting, setExporting] = useState(false);
    const [selectedDatasets, setSelectedDatasets] = useState<SelectedDatasets>(
        {}
    );

    const setDatasourceModal = useSetRecoilState(datasourceModalOpenState);
    const activeItems = useActiveItems();

    const {
        setEnvironmentPrimaryImage,
        setEnvironmentStructureSet,
        setEnvironmentDose,
    } = useViewportEnvironment();

    const handleCheckSelect = (
        _: React.MouseEvent<HTMLElement, MouseEvent>,
        series: SeriesData
    ) => {
        const modalityData = selectedDatasets[series.modality] || {};
        const seriesIds = Object.keys(modalityData);
        if (seriesIds.some((value) => value === series.id)) {
            setSelectedDatasets((selected) => {
                // Clone the relevant modality object to avoid directly mutating state
                const updatedModalityData = { ...modalityData };
                // Use delete to remove the series from the cloned object
                delete updatedModalityData[series.id];
                return {
                    ...selected,
                    [series.modality]: updatedModalityData,
                };
            });
        } else {
            setSelectedDatasets((selected) => ({
                ...selected,
                [series.modality]: {
                    ...modalityData,
                    [series.id]: series.seriesDataObject,
                },
            }));
        }
    };

    const handleDownloadDatasets = async () => {
        try {
            setExporting(true);
            await downloadItemsToDisk(
                createExportItems(selectedDatasets),
                `${patient.dataSet.PatientName}_export`
            );
            setExporting(false);
        } catch (error) {
            handleError(error, 'Exporting files failed, please try again.');
        }
    };

    const handleLoadDatasets = () => {
        const exportItems = createExportItems(selectedDatasets);
        setDatasourceModal(false);

        // Load the first item of each type
        if (exportItems.images && exportItems.images.length > 0) {
            setEnvironmentPrimaryImage(
                [exportItems.images[0]],
                patient.dataSet.PatientID
            );
        }
        if (exportItems.structureSets && exportItems.structureSets.length > 0) {
            setEnvironmentStructureSet(
                [exportItems.structureSets[0]],
                patient.dataSet.PatientID
            );
        }
        if (exportItems.doses && exportItems.doses.length > 0) {
            setEnvironmentDose(
                [exportItems.doses[0]],
                patient.dataSet.PatientID
            );
        }
    };

    const datasetSelected = Object.keys(selectedDatasets).length > 0;
    const data = transformPatientData(patient);

    return (
        <PatientAccordion patientKey={patient.id} patientData={data}>
            {data?.series ? (
                <PatientDataTable
                    rows={data.series}
                    selectedDatasets={selectedDatasets}
                    onCheckSelect={handleCheckSelect}
                    activeItems={activeItems}
                />
            ) : (
                <Typography>No datasets for this patient.</Typography>
            )}
            <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                <Button
                    onClick={handleDownloadDatasets}
                    variant="text"
                    color="primary"
                    size="small"
                    disabled={!datasetSelected || exporting}
                    startIcon={
                        exporting ? (
                            <CircularProgress size={12} />
                        ) : (
                            <Download size={12} />
                        )
                    }
                >
                    <FormattedMessage id="downloadDatasets" />
                </Button>
                <Button
                    onClick={handleLoadDatasets}
                    variant="outlined"
                    color="primary"
                    size="small"
                    disabled={!datasetSelected || exporting}
                >
                    <FormattedMessage id="load" />
                </Button>
            </Box>
        </PatientAccordion>
    );
}
